import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu } from 'lucide-react';
import { categoryService } from '../../services/categoryService';

// Barra de navegação secundária do storefront (linha 2 do StorefrontHeader).
// Inspirada no mockup: "Todas categorias" (mega-menu) + Novidades, Mais
// vendidos, Ofertas, Lançamentos, Marcas. Cor primária do tema da loja.
const SHORTCUTS = [
  { label: 'Novidades', to: '/catalogo?sort=newest' },
  { label: 'Mais vendidos', to: '/catalogo?sort=bestSelling' },
  { label: 'Ofertas', to: '/catalogo?onSale=true' },
  { label: 'Lançamentos', to: () => {
    const since = new Date();
    since.setDate(since.getDate() - 60);
    return `/catalogo?publishedSince=${since.toISOString().slice(0, 10)}`;
  }},
];

const CategoryNavBar = () => {
  const [categories, setCategories] = useState([]);
  const [openMega, setOpenMega] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const megaRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    categoryService.tree().then((data) => {
      if (cancelled) return;
      const list = Array.isArray(data) ? data : data?.rows || [];
      setCategories(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setOpenMega(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Fecha drawer ao mudar de rota.
  useEffect(() => {
    setOpenMobile(false);
    setOpenMega(false);
  }, [location.pathname, location.search]);

  return (
    <nav className="bg-primary text-white">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        <div className="flex items-center h-9">
          {/* Mobile: hamburguer abre drawer com tudo */}
          <button
            className="md:hidden inline-flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
            onClick={() => setOpenMobile((v) => !v)}
            aria-label="Abrir menu de categorias"
          >
            <Menu size={18} />
            <span className="text-sm font-medium">Categorias</span>
          </button>

          {/* Desktop: mega-menu de categorias + atalhos */}
          <div className="hidden md:flex items-center gap-1 w-full" ref={megaRef}>
            <button
              onClick={() => setOpenMega((v) => !v)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
            >
              Todas categorias
              <ChevronDown size={14} className={openMega ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </button>

            {SHORTCUTS.map((s) => {
              const to = typeof s.to === 'function' ? s.to() : s.to;
              return (
                <Link
                  key={s.label}
                  to={to}
                  className="px-3 py-1.5 rounded-md hover:bg-white/10 text-sm font-medium transition-colors"
                >
                  {s.label}
                </Link>
              );
            })}

            <div className="ml-auto" />

            {openMega && (
              <div className="absolute left-0 right-0 top-full bg-white text-app-text shadow-2xl border-b border-app-border z-40">
                <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 py-6">
                    {categories.length === 0 ? (
                      <p className="col-span-full text-sm text-app-text-secondary">
                        Nenhuma categoria cadastrada ainda.
                      </p>
                    ) : (
                      categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/catalogo?categoryId=${cat.id}`}
                          className="block py-2 hover:text-primary transition-colors"
                        >
                          <span className="font-semibold text-sm">{cat.name}</span>
                          {Array.isArray(cat.children) && cat.children.length > 0 && (
                            <ul className="mt-1 ml-1 space-y-0.5">
                              {cat.children.slice(0, 5).map((sub) => (
                                <li key={sub.id} className="text-xs text-app-text-secondary hover:text-primary">
                                  {sub.name}
                                </li>
                              ))}
                            </ul>
                          )}
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {openMobile && (
        <div className="md:hidden bg-white text-app-text border-t border-white/20 max-h-[60vh] overflow-y-auto">
          <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
            <div className="py-3 space-y-1">
              <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wider px-2 pt-2 pb-1">
                Atalhos
              </p>
              {SHORTCUTS.map((s) => {
                const to = typeof s.to === 'function' ? s.to() : s.to;
                return (
                  <Link
                    key={s.label}
                    to={to}
                    className="block px-2 py-2 rounded-md text-sm font-medium hover:bg-primary/5"
                  >
                    {s.label}
                  </Link>
                );
              })}
              <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wider px-2 pt-3 pb-1">
                Categorias
              </p>
              {categories.length === 0 ? (
                <p className="px-2 text-sm text-app-text-secondary">Nenhuma categoria</p>
              ) : (
                categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/catalogo?categoryId=${cat.id}`}
                    className="block px-2 py-2 rounded-md text-sm hover:bg-primary/5"
                  >
                    {cat.name}
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default CategoryNavBar;
